import { useEffect, useRef, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { calcQuantity } from '../../utils/format';
import FloatingPanel, { isInsideFloatingPanel } from '../common/FloatingPanel';

const TYPES = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drops', 'Ointment', 'Powder', 'Inhaler', 'Other'];
const TIMINGS = ['Before Food', 'After Food', 'With Food', 'Empty Stomach', 'Bedtime', 'Any Time'];
/** morning-noon-night. The label spells it out so nobody has to guess. */
const FREQUENCIES = [
  { value: '1-1-1', label: 'Morning, noon and night' },
  { value: '1-0-1', label: 'Morning and night' },
  { value: '1-0-0', label: 'Morning only' },
  { value: '0-1-0', label: 'Noon only' },
  { value: '0-0-1', label: 'Night only' },
  { value: '1-1-0', label: 'Morning and noon' },
  { value: '0-1-1', label: 'Noon and night' },
  { value: '2-2-2', label: 'Two, three times a day' },
  { value: '1-1-1-1', label: 'Four times a day' },
  { value: 'SOS', label: 'Only when needed' },
  { value: 'STAT', label: 'At once, single dose' },
];

/**
 * One line of the prescription. Typing in the name field searches the medicine
 * master and picking a suggestion fills in the doctor's usual dose for it.
 */
export default function MedicineRow({ value, index, medicines, onChange, onRemove, isLast, onAddRow }) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [freqOpen, setFreqOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const freqRef = useRef(null);

  useEffect(() => {
    function away(e) {
      // The panels live outside this row in the DOM, so let their clicks through.
      if (isInsideFloatingPanel(e.target)) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setSuggestOpen(false);
      if (freqRef.current && !freqRef.current.contains(e.target)) setFreqOpen(false);
    }
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const matches =
    value.name.trim().length >= 1
      ? medicines
          .filter((m) => m.name.toLowerCase().includes(value.name.trim().toLowerCase()))
          .slice(0, 8)
      : [];

  function set(field, v) {
    const next = { ...value, [field]: v };
    // Keep the chemist's count in step unless the doctor typed one by hand.
    if (['frequency', 'duration', 'durationUnit'].includes(field)) {
      next.quantity = calcQuantity(next);
    }
    onChange(index, next);
  }

  function applySuggestion(m) {
    onChange(index, {
      ...value,
      name: m.name,
      type: m.type || value.type,
      dose: m.defaultDose || value.dose,
      frequency: m.defaultFrequency || value.frequency,
      timing: m.defaultTiming || value.timing,
      duration: m.defaultDuration || value.duration,
      quantity: calcQuantity({
        ...value,
        frequency: m.defaultFrequency || value.frequency,
        duration: m.defaultDuration || value.duration,
      }),
    });
    setSuggestOpen(false);
  }

  function onNameKeyDown(e) {
    if (suggestOpen && matches.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (h - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        applySuggestion(matches[highlight]);
        return;
      }
      if (e.key === 'Escape') {
        setSuggestOpen(false);
        return;
      }
    }
    // Enter on the last row adds another one, so the doctor never reaches for the mouse.
    if (e.key === 'Enter' && isLast && value.name.trim()) {
      e.preventDefault();
      onAddRow();
    }
  }

  return (
    <tr>
      <td className="text-muted small text-center align-middle">{index + 1}</td>

      <td style={{ minWidth: 200 }}>
        <div className="position-relative" ref={wrapRef}>
          <Form.Control
            size="sm"
            value={value.name}
            placeholder="Start typing the medicine name"
            onChange={(e) => {
              set('name', e.target.value);
              setSuggestOpen(true);
              setHighlight(0);
            }}
            onFocus={() => setSuggestOpen(true)}
            onKeyDown={onNameKeyDown}
          />
          <FloatingPanel anchorRef={wrapRef} open={suggestOpen && matches.length > 0} minWidth={260}>
            {matches.map((m, i) => (
              <button
                type="button"
                key={m._id}
                className={i === highlight ? 'active' : ''}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => applySuggestion(m)}
              >
                <span className="fw-semibold">{m.name}</span>
                <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>
                  {m.type}
                  {m.defaultFrequency ? ` · ${m.defaultFrequency}` : ''}
                </span>
              </button>
            ))}
          </FloatingPanel>
        </div>
      </td>

      <td style={{ width: 118 }}>
        <Form.Select size="sm" value={value.type} onChange={(e) => set('type', e.target.value)}>
          {TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </Form.Select>
      </td>

      <td style={{ width: 88 }}>
        <Form.Control size="sm" value={value.dose} placeholder="1 tab" onChange={(e) => set('dose', e.target.value)} />
      </td>

      <td style={{ width: 118 }}>
        <div className="position-relative" ref={freqRef}>
          <InputGroup size="sm" className="flex-nowrap">
            <Form.Control
              value={value.frequency}
              placeholder="1-0-1"
              title={FREQUENCIES.find((f) => f.value === value.frequency)?.label || 'morning-noon-night'}
              onChange={(e) => set('frequency', e.target.value)}
              onFocus={() => setFreqOpen(true)}
            />
            <Button
              variant="outline-secondary"
              className="px-1"
              tabIndex={-1}
              onClick={() => setFreqOpen((o) => !o)}
              aria-label="Choose how often"
            >
              <i className="bi bi-caret-down-fill" style={{ fontSize: '0.6rem' }} />
            </Button>
          </InputGroup>

          <FloatingPanel anchorRef={freqRef} open={freqOpen} minWidth={230}>
            {FREQUENCIES.map((f) => (
              <button
                type="button"
                key={f.value}
                className={f.value === value.frequency ? 'active' : ''}
                onClick={() => {
                  set('frequency', f.value);
                  setFreqOpen(false);
                }}
              >
                <span className="fw-semibold">{f.value}</span>
                <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>
                  {f.label}
                </span>
              </button>
            ))}
          </FloatingPanel>
        </div>
      </td>

      <td style={{ width: 150 }}>
        <Form.Select size="sm" value={value.timing} onChange={(e) => set('timing', e.target.value)}>
          {TIMINGS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </Form.Select>
      </td>

      <td style={{ width: 152 }}>
        <div className="d-flex gap-1">
          <Form.Control
            size="sm"
            type="number"
            min={1}
            value={value.duration}
            onChange={(e) => set('duration', Number(e.target.value))}
            style={{ width: 58 }}
          />
          <Form.Select
            size="sm"
            value={value.durationUnit}
            onChange={(e) => set('durationUnit', e.target.value)}
            style={{ width: 86 }}
          >
            <option value="days">days</option>
            <option value="weeks">wks</option>
            <option value="months">mths</option>
          </Form.Select>
        </div>
      </td>

      <td style={{ width: 76 }}>
        <Form.Control
          size="sm"
          type="number"
          value={value.quantity}
          onChange={(e) => set('quantity', Number(e.target.value))}
        />
      </td>

      <td style={{ width: 140 }}>
        <Form.Control
          size="sm"
          value={value.instruction}
          placeholder="Note"
          onChange={(e) => set('instruction', e.target.value)}
        />
      </td>

      <td style={{ width: 40 }}>
        <Button size="sm" variant="outline-danger" onClick={() => onRemove(index)} title="Remove this medicine">
          <i className="bi bi-trash" />
        </Button>
      </td>
    </tr>
  );
}

export const EMPTY_MEDICINE = {
  name: '',
  type: 'Tablet',
  dose: '1 tab',
  frequency: '1-0-1',
  timing: 'After Food',
  duration: 5,
  durationUnit: 'days',
  quantity: 10,
  instruction: '',
};
